import React, { useState } from 'react';

import Menu from 'components/shared/Menu';
import Alert from 'components/shared/Alert';
import EditProjectName from './EditProjectName';
import SaveProjectModal from './SaveProjectModal';
import LoadProjectModal from './LoadProjectModal';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import cartModule from 'domains/cart';
import session from 'domains/session';
import projects from 'domains/projects';

import type { PickRequired } from 'types/common_types';
import type { Project, ProjectSaveParams } from 'domains/projects';

type ProjectControlsProps = {
  onShowProjects: () => void;
};

const alertMessage =
  'Starting a new project will remove all items from your current cart. All unsaved changes will be lost. Do you wish to continue?';

// most recent TS version does not need a type guard for this, but we are stuck
// due to ts-loader incompatibility. should be resolved soon
function hasIDs(p: ProjectSaveParams): p is PickRequired<ProjectSaveParams, 'guid' | 'id'> {
  return !!p.guid && !!p.id;
}

function ProjectControls(props: ProjectControlsProps) {
  const { onShowProjects } = props;

  const dispatch = useAppDispatch();

  const cartProjectId = useAppSelector(cartModule.selectors.projectId);
  const isLoggedIn = useAppSelector(session.selectors.isLoggedIn);
  const cartProject = useAppSelector((state) =>
    projects.selectors.selectById(state, cartProjectId || '')
  );
  const allProjects = useAppSelector(projects.selectors.selectAll);

  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);

  const [disableProjects, setDisableProjects] = useState(false);

  function handleSaveProject(project: ProjectSaveParams) {
    if (hasIDs(project)) {
      dispatch(projects.actions.update.pending(project));
    } else {
      dispatch(projects.actions.create.pending(project));
    }
  }

  function handleDeleteProject(project: Project) {
    dispatch(projects.actions.destroy.pending(project));
  }

  function handleRestoreProject(project: Project) {
    dispatch(projects.actions.restore.pending(project.guid));
  }

  function handleCreateNewProject() {
    setShowNewDialog(false);
    setDisableProjects(true);
    window.location.assign('/cart/delete');
  }

  const menuItems = [
    <button role="menuitem" onClick={() => setShowSaveDialog(true)}>
      Save Project
    </button>,
    <button role="menuitem" onClick={() => setShowLoadDialog(true)}>
      Load Project
    </button>,
    <button role="menuitem" onClick={() => setShowNewDialog(true)}>
      New Project
    </button>,
  ];

  if (cartProject) {
    menuItems.unshift(<EditProjectName project={cartProject} onSave={handleSaveProject} />);
  }

  return (
    <React.Fragment>
      <Menu
        triggerId="projects-menu-trigger"
        triggerText={isLoggedIn ? 'Projects' : 'Sign in to save projects'}
        triggerIcon="folder"
        className="projects-menu"
        disabled={!isLoggedIn || disableProjects}
        appearance="dark"
        direction="down"
        position="left"
        menuItems={menuItems}
      />

      {showNewDialog &&
        renderModal(
          <Alert
            title="New Project"
            labelledBy="new-project-alert-modal"
            lastFocusedElementId="projects-menu-trigger"
            message={alertMessage}
            onContinue={handleCreateNewProject}
            onClose={() => setShowNewDialog(false)}
            onCancel={() => setShowNewDialog(false)}
          />
        )}

      {showSaveDialog &&
        renderModal(
          <SaveProjectModal
            cartProject={cartProject}
            onSave={handleSaveProject}
            onClose={() => setShowSaveDialog(false)}
          />
        )}

      {showLoadDialog &&
        renderModal(
          <LoadProjectModal
            projects={allProjects}
            onShowProjects={onShowProjects}
            onRestore={handleRestoreProject}
            onDelete={handleDeleteProject}
            onClose={() => setShowLoadDialog(false)}
          />
        )}
    </React.Fragment>
  );
}

export default ProjectControls;
